import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";

const AdvancedSearch = () => {
  const { user: authUser, fetchWithRetry, isLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("search"); // Tab hiện tại: "search", "saved", "popular"
  const [searchCriteria, setSearchCriteria] = useState({
    ageRange: { minAge: 18, maxAge: 100 },
    gender: "",
    nationality: "",
    occupation: "",
    education: "",
    religion: "",
    maritalStatus: "",
    hasChildren: "",
    drinking: "",
    smoking: "",
    willingToRelocate: "",
    zodiacSign: "",
    lunarYear: "",
    locationText: { country: "", city: "", district: "", ward: "" },
    currentLocation: { country: "", city: "", district: "", ward: "", coordinates: [0, 0] },
    appearance: {
      hairColor: "",
      eyeColor: "",
      height: { min: 0, max: 300 },
      weight: { min: 0, max: 300 },
      bodyType: "",
      ethnicity: "",
      bodyArt: "",
      appearanceRating: "",
    },
    lifestyle: {
      drinking: "",
      smoking: "",
      maritalStatus: "",
      hasChildren: "",
      numberOfChildren: "",
      oldestChild: "",
      youngestChild: "",
      wantsChildren: "",
      hasPets: "",
      occupation: "",
    },
    culture: {
      nationality: "",
      education: "",
      primaryLanguage: "",
      englishAbility: "",
      vietnameseAbility: "",
      religion: "",
    },
  });
  const [savedSearches, setSavedSearches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [editingSearch, setEditingSearch] = useState(null); // State để lưu tìm kiếm đang chỉnh sửa

  // Tải thông báo
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithRetry({
        method: "get",
        url: "/api/users/notifications",
      });
      setNotifications(response.data);
    } catch (error) {
      console.error("Lỗi khi tải thông báo:", error);
      setError("Không thể tải thông báo. Vui lòng thử lại sau.");
    } finally {
      setLoading(false);
    }
  }, [fetchWithRetry]);

  // Tải danh sách tìm kiếm đã lưu từ backend
  const fetchSavedSearches = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchWithRetry({
        method: "get",
        url: "/api/users/saved-searches",
      });
      setSavedSearches(response.data.savedSearches || []);
    } catch (error) {
      console.error("Lỗi khi tải tìm kiếm đã lưu:", error);
      setError("Không thể tải tìm kiếm đã lưu. Vui lòng thử lại sau.");
    } finally {
      setLoading(false);
    }
  }, [fetchWithRetry]);

  // Khởi tạo dữ liệu khi component mount
  useEffect(() => {
    if (isLoading) return;
    if (!authUser || !authUser._id) {
      navigate("/login");
      return;
    }

    fetchNotifications();
    fetchSavedSearches();
  }, [authUser, isLoading, navigate, fetchNotifications, fetchSavedSearches]);

  const unreadCount = 0;
  const unreadNotificationCount = notifications.filter((n) => !n.read).length;

  const handleToggleNotifications = (notificationId, action) => {
    if (action === "read") {
      setNotifications((prev) =>
        prev.map((n) => (n._id === notificationId ? { ...n, read: true } : n))
      );
    } else if (action === "delete") {
      setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
    }
  };

  // Xử lý thay đổi tiêu chí tìm kiếm
  const handleCriteriaChange = (e) => {
    const { name, value } = e.target;
    if (name === "minAge" || name === "maxAge") {
      setSearchCriteria((prev) => ({
        ...prev,
        ageRange: {
          ...prev.ageRange,
          [name]: parseInt(value) || 0,
        },
      }));
    } else if (name.startsWith("locationText.")) {
      const field = name.split(".")[1];
      setSearchCriteria((prev) => ({
        ...prev,
        locationText: {
          ...prev.locationText,
          [field]: value,
        },
      }));
    } else if (name.startsWith("currentLocation.")) {
      const field = name.split(".")[1];
      setSearchCriteria((prev) => ({
        ...prev,
        currentLocation: {
          ...prev.currentLocation,
          [field]: value,
        },
      }));
    } else if (name.startsWith("appearance.")) {
      const field = name.split(".")[1];
      if (field === "heightMin" || field === "heightMax") {
        setSearchCriteria((prev) => ({
          ...prev,
          appearance: {
            ...prev.appearance,
            height: {
              ...prev.appearance.height,
              [field === "heightMin" ? "min" : "max"]: parseInt(value) || 0,
            },
          },
        }));
      } else if (field === "weightMin" || field === "weightMax") {
        setSearchCriteria((prev) => ({
          ...prev,
          appearance: {
            ...prev.appearance,
            weight: {
              ...prev.appearance.weight,
              [field === "weightMin" ? "min" : "max"]: parseInt(value) || 0,
            },
          },
        }));
      } else {
        setSearchCriteria((prev) => ({
          ...prev,
          appearance: {
            ...prev.appearance,
            [field]: value,
          },
        }));
      }
    } else if (name.startsWith("lifestyle.")) {
      const field = name.split(".")[1];
      setSearchCriteria((prev) => ({
        ...prev,
        lifestyle: {
          ...prev.lifestyle,
          [field]: value,
        },
      }));
    } else if (name.startsWith("culture.")) {
      const field = name.split(".")[1];
      setSearchCriteria((prev) => ({
        ...prev,
        culture: {
          ...prev.culture,
          [field]: value,
        },
      }));
    } else {
      setSearchCriteria((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Lưu tìm kiếm vào backend
  const handleSaveSearch = async () => {
    // Lấy tên tìm kiếm từ người dùng
    const searchName = prompt("Nhập tên cho tìm kiếm này:");
    if (!searchName || searchName.trim() === "") {
      setError("Tên tìm kiếm không được để trống!");
      return;
    }

    // Kiểm tra xem criteria có dữ liệu hợp lệ không
    if (!searchCriteria || typeof searchCriteria !== "object" || Object.keys(searchCriteria).length === 0) {
      setError("Tiêu chí tìm kiếm không hợp lệ hoặc rỗng!");
      return;
    }

    // Kiểm tra createdAt
    const createdAt = new Date().toISOString();
    if (!createdAt || isNaN(new Date(createdAt).getTime())) {
      setError("Ngày tạo tìm kiếm không hợp lệ!");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const newSearch = {
        name: searchName.trim(),
        criteria: searchCriteria,
        createdAt,
      };

      console.log("Đang gửi yêu cầu lưu tìm kiếm:", JSON.stringify(newSearch, null, 2));

      const response = await fetchWithRetry({
        method: "post",
        url: "/api/users/save-search",
        data: newSearch,
      });

      console.log("Phản hồi từ API lưu tìm kiếm:", JSON.stringify(response.data, null, 2));

      setSavedSearches(response.data.savedSearches || []);
      setSuccess("Lưu tìm kiếm thành công!");
    } catch (error) {
      console.error("Chi tiết lỗi khi lưu tìm kiếm:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack,
      });

      // Hiển thị thông báo lỗi chi tiết từ backend nếu có
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Không thể lưu tìm kiếm do yêu cầu không hợp lệ hoặc tài nguyên không tồn tại.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Xóa tìm kiếm đã lưu
  const handleDeleteSavedSearch = async (index) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      console.log(`Đang xóa tìm kiếm tại chỉ số ${index}`);
      const response = await fetchWithRetry({
        method: "delete",
        url: `/api/users/delete-search/${index}`,
      });
      console.log("Phản hồi từ API xóa tìm kiếm:", JSON.stringify(response.data, null, 2));
      setSavedSearches(response.data.savedSearches || []);
      setSuccess("Xóa tìm kiếm thành công!");
    } catch (error) {
      console.error("Chi tiết lỗi khi xóa tìm kiếm:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack,
      });
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Không thể xóa tìm kiếm do yêu cầu không hợp lệ hoặc tài nguyên không tồn tại.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Bắt đầu chỉnh sửa tìm kiếm
  const handleEditSavedSearch = (index) => {
    setEditingSearch({ index, ...savedSearches[index] });
    setSearchCriteria(savedSearches[index].criteria);
    setActiveTab("search"); // Chuyển về tab "Tìm kiếm nâng cao" để chỉnh sửa
  };

  // Lưu tìm kiếm đã chỉnh sửa
  const handleUpdateSearch = async () => {
    if (!editingSearch) {
      setError("Không có tìm kiếm nào đang được chỉnh sửa!");
      return;
    }

    const searchName = prompt("Nhập tên mới cho tìm kiếm này:", editingSearch.name);
    if (!searchName || searchName.trim() === "") {
      setError("Tên tìm kiếm không được để trống!");
      return;
    }

    // Kiểm tra xem criteria có dữ liệu hợp lệ không
    if (!searchCriteria || typeof searchCriteria !== "object" || Object.keys(searchCriteria).length === 0) {
      setError("Tiêu chí tìm kiếm không hợp lệ hoặc rỗng!");
      return;
    }

    // Kiểm tra createdAt
    const createdAt = new Date().toISOString();
    if (!createdAt || isNaN(new Date(createdAt).getTime())) {
      setError("Ngày tạo tìm kiếm không hợp lệ!");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      // Xóa tìm kiếm cũ trước khi lưu tìm kiếm mới
      await fetchWithRetry({
        method: "delete",
        url: `/api/users/delete-search/${editingSearch.index}`,
      });

      const updatedSearch = {
        name: searchName.trim(),
        criteria: searchCriteria,
        createdAt,
      };

      console.log("Đang gửi yêu cầu cập nhật tìm kiếm:", JSON.stringify(updatedSearch, null, 2));

      const response = await fetchWithRetry({
        method: "post",
        url: "/api/users/save-search",
        data: updatedSearch,
      });

      console.log("Phản hồi từ API cập nhật tìm kiếm:", JSON.stringify(response.data, null, 2));

      setSavedSearches(response.data.savedSearches || []);
      setEditingSearch(null);
      setSuccess("Cập nhật tìm kiếm thành công!");
    } catch (error) {
      console.error("Chi tiết lỗi khi cập nhật tìm kiếm:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack,
      });
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Không thể cập nhật tìm kiếm do yêu cầu không hợp lệ hoặc tài nguyên không tồn tại.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Hủy chỉnh sửa
  const handleCancelEdit = () => {
    setEditingSearch(null);
    setSearchCriteria({
      ageRange: { minAge: 18, maxAge: 100 },
      gender: "",
      nationality: "",
      occupation: "",
      education: "",
      religion: "",
      maritalStatus: "",
      hasChildren: "",
      drinking: "",
      smoking: "",
      willingToRelocate: "",
      zodiacSign: "",
      lunarYear: "",
      locationText: { country: "", city: "", district: "", ward: "" },
      currentLocation: { country: "", city: "", district: "", ward: "", coordinates: [0, 0] },
      appearance: {
        hairColor: "",
        eyeColor: "",
        height: { min: 0, max: 300 },
        weight: { min: 0, max: 300 },
        bodyType: "",
        ethnicity: "",
        bodyArt: "",
        appearanceRating: "",
      },
      lifestyle: {
        drinking: "",
        smoking: "",
        maritalStatus: "",
        hasChildren: "",
        numberOfChildren: "",
        oldestChild: "",
        youngestChild: "",
        wantsChildren: "",
        hasPets: "",
        occupation: "",
      },
      culture: {
        nationality: "",
        education: "",
        primaryLanguage: "",
        englishAbility: "",
        vietnameseAbility: "",
        religion: "",
      },
    });
  };

  // Tìm kiếm và điều hướng
  const handleSearch = () => {
    navigate("/search-results", { state: { criteria: searchCriteria } });
  };

  // Chọn tìm kiếm đã lưu và điều hướng
  const handleSelectSavedSearch = (criteria) => {
    navigate("/search-results", { state: { criteria } });
  };

  // Chọn tìm kiếm phổ biến và điều hướng
  const handlePopularSearch = (sortType) => {
    navigate("/search-results", { state: { criteria: searchCriteria, sortType } });
  };

  if (isLoading || loading) {
    return <p className="text-center text-gray-600">Đang tải...</p>;
  }

  return (
    <div className="font-sans min-h-screen bg-gradient-to-b from-gray-100 to-gray-200">
      <Navbar
        unreadCount={unreadCount}
        unreadNotificationCount={unreadNotificationCount}
        notifications={notifications}
        onToggleNotifications={handleToggleNotifications}
      />

      <div className="pt-20 max-w-5xl mx-auto p-6">
        <h2 className="text-4xl font-bold text-center text-gray-800 mb-8">Tìm Kiếm Nâng Cao</h2>

        {error && <p className="text-red-500 text-center mb-4">{error}</p>}
        {success && <p className="text-green-500 text-center mb-4">{success}</p>}

        {/* Tabs */}
        <div className="flex border-b mb-6">
          <button
            onClick={() => setActiveTab("search")}
            className={`px-4 py-2 font-medium ${
              activeTab === "search"
                ? "border-b-2 border-blue-500 text-blue-600"
                : "text-gray-600 hover:text-blue-600"
            }`}
          >
            Tìm kiếm nâng cao
          </button>
          <button
            onClick={() => setActiveTab("saved")}
            className={`px-4 py-2 font-medium ${
              activeTab === "saved"
                ? "border-b-2 border-blue-500 text-blue-600"
                : "text-gray-600 hover:text-blue-600"
            }`}
          >
            Tìm kiếm đã lưu
          </button>
          <button
            onClick={() => setActiveTab("popular")}
            className={`px-4 py-2 font-medium ${
              activeTab === "popular"
                ? "border-b-2 border-blue-500 text-blue-600"
                : "text-gray-600 hover:text-blue-600"
            }`}
          >
            Tìm kiếm phổ biến
          </button>
        </div>

        {/* Tab 1: Tìm kiếm nâng cao */}
        {activeTab === "search" && (
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Độ tuổi */}
              <div>
                <label className="block text-gray-700 font-medium mb-1">Độ tuổi tối thiểu:</label>
                <input
                  type="number"
                  name="minAge"
                  value={searchCriteria.ageRange?.minAge || 18}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                  min="18"
                  max="100"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Độ tuổi tối đa:</label>
                <input
                  type="number"
                  name="maxAge"
                  value={searchCriteria.ageRange?.maxAge || 100}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                  min="18"
                  max="100"
                />
              </div>

              {/* Giới tính */}
              <div>
                <label className="block text-gray-700 font-medium mb-1">Giới tính:</label>
                <select
                  name="gender"
                  value={searchCriteria.gender || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="male">Nam</option>
                  <option value="female">Nữ</option>
                  <option value="other">Khác</option>
                </select>
              </div>

              {/* Vị trí */}
              <div>
                <label className="block text-gray-700 font-medium mb-1">Quốc gia:</label>
                <select
                  name="locationText.country"
                  value={searchCriteria.locationText?.country || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="Việt Nam">Việt Nam</option>
                  <option value="Afghanistan">Afghanistan</option>
                  <option value="Ai Cập">Ai Cập</option>
                  <option value="Albania">Albania</option>
                  <option value="Algeria">Algeria</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Thành phố:</label>
                <select
                  name="locationText.city"
                  value={searchCriteria.locationText?.city || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="Hồ Chí Minh">Hồ Chí Minh</option>
                  <option value="Hà Nội">Hà Nội</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Quận/Huyện:</label>
                <select
                  name="locationText.district"
                  value={searchCriteria.locationText?.district || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="Quận 1">Quận 1</option>
                  <option value="Quận 3">Quận 3</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Xã/Phường:</label>
                <input
                  type="text"
                  name="locationText.ward"
                  value={searchCriteria.locationText?.ward || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                  placeholder="Nhập xã/phường hoặc để trống"
                />
              </div>

              {/* Diện mạo */}
              <div>
                <label className="block text-gray-700 font-medium mb-1">Chiều cao tối thiểu (cm):</label>
                <input
                  type="number"
                  name="appearance.heightMin"
                  value={searchCriteria.appearance?.height?.min || 0}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                  min="0"
                  max="300"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Chiều cao tối đa (cm):</label>
                <input
                  type="number"
                  name="appearance.heightMax"
                  value={searchCriteria.appearance?.height?.max || 300}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                  min="0"
                  max="300"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Cân nặng tối thiểu (kg):</label>
                <input
                  type="number"
                  name="appearance.weightMin"
                  value={searchCriteria.appearance?.weight?.min || 0}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                  min="0"
                  max="300"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Cân nặng tối đa (kg):</label>
                <input
                  type="number"
                  name="appearance.weightMax"
                  value={searchCriteria.appearance?.weight?.max || 300}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                  min="0"
                  max="300"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Màu tóc:</label>
                <select
                  name="appearance.hairColor"
                  value={searchCriteria.appearance?.hairColor || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="Hói / Cạo">Hói / Cạo</option>
                  <option value="Nâu">Nâu</option>
                  <option value="Nâu sáng">Nâu sáng</option>
                  <option value="Vàng">Vàng</option>
                  <option value="Xám / Trắng">Xám / Trắng</option>
                  <option value="Đen">Đen</option>
                  <option value="Đỏ">Đỏ</option>
                  <option value="Thường xuyên thay đổi">Thường xuyên thay đổi</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Màu mắt:</label>
                <select
                  name="appearance.eyeColor"
                  value={searchCriteria.appearance?.eyeColor || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="Nâu">Nâu</option>
                  <option value="Nâu lục nhạt">Nâu lục nhạt</option>
                  <option value="Xanh lam">Xanh lam</option>
                  <option value="Xanh lục">Xanh lục</option>
                  <option value="Xám">Xám</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Thân hình:</label>
                <select
                  name="appearance.bodyType"
                  value={searchCriteria.appearance?.bodyType || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="slim">Gầy</option>
                  <option value="athletic">Lực lưỡng</option>
                  <option value="average">Trung bình</option>
                  <option value="heavy">Nặng ký</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Sắc tộc:</label>
                <select
                  name="appearance.ethnicity"
                  value={searchCriteria.appearance?.ethnicity || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="Châu Á">Châu Á</option>
                  <option value="Da trắng">Da trắng</option>
                  <option value="La tinh">La tinh</option>
                  <option value="Lai">Lai</option>
                  <option value="Phi">Phi</option>
                  <option value="Thái Bình Dương">Thái Bình Dương</option>
                  <option value="Ả rập (Trung Đông)">Ả rập (Trung Đông)</option>
                  <option value="Ấn Độ">Ấn Độ</option>
                  <option value="Khác">Khác</option>
                  <option value="Không thích nói">Không thích nói</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Hình xăm/Khuyên:</label>
                <select
                  name="appearance.bodyArt"
                  value={searchCriteria.appearance?.bodyArt || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="Hình xăm">Hình xăm</option>
                  <option value="Khuyên">Khuyên</option>
                  <option value="Khuyên tai">Khuyên tai</option>
                  <option value="Đóng dấu">Đóng dấu</option>
                  <option value="Khác">Khác</option>
                  <option value="Không">Không</option>
                  <option value="Không thích nói">Không thích nói</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Đánh giá diện mạo:</label>
                <select
                  name="appearance.appearanceRating"
                  value={searchCriteria.appearance?.appearanceRating || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="Dưới mức trung bình">Dưới mức trung bình</option>
                  <option value="Trung bình">Trung bình</option>
                  <option value="Thu hút">Thu hút</option>
                  <option value="Rất thu hút">Rất thu hút</option>
                </select>
              </div>

              {/* Phong cách sống */}
              <div>
                <label className="block text-gray-700 font-medium mb-1">Uống rượu:</label>
                <select
                  name="lifestyle.drinking"
                  value={searchCriteria.lifestyle?.drinking || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="yes">Có</option>
                  <option value="no">Không</option>
                  <option value="occasionally">Thỉnh thoảng</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Hút thuốc:</label>
                <select
                  name="lifestyle.smoking"
                  value={searchCriteria.lifestyle?.smoking || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="yes">Có</option>
                  <option value="no">Không</option>
                  <option value="occasionally">Thỉnh thoảng</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Tình trạng hôn nhân:</label>
                <select
                  name="lifestyle.maritalStatus"
                  value={searchCriteria.lifestyle?.maritalStatus || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="single">Độc thân</option>
                  <option value="married">Đã kết hôn</option>
                  <option value="divorced">Ly hôn</option>
                  <option value="widowed">Góa</option>
                  <option value="other">Khác</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Có con:</label>
                <select
                  name="lifestyle.hasChildren"
                  value={searchCriteria.lifestyle?.hasChildren || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="yes">Có</option>
                  <option value="no">Không</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Số lượng con:</label>
                <select
                  name="lifestyle.numberOfChildren"
                  value={searchCriteria.lifestyle?.numberOfChildren || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="0">0</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5+">5 trở lên</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Con lớn nhất:</label>
                <select
                  name="lifestyle.oldestChild"
                  value={searchCriteria.lifestyle?.oldestChild || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="0-5">0-5 tuổi</option>
                  <option value="6-10">6-10 tuổi</option>
                  <option value="11-15">11-15 tuổi</option>
                  <option value="16-20">16-20 tuổi</option>
                  <option value="21+">21 tuổi trở lên</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Con nhỏ nhất:</label>
                <select
                  name="lifestyle.youngestChild"
                  value={searchCriteria.lifestyle?.youngestChild || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="0-5">0-5 tuổi</option>
                  <option value="6-10">6-10 tuổi</option>
                  <option value="11-15">11-15 tuổi</option>
                  <option value="16-20">16-20 tuổi</option>
                  <option value="21+">21 tuổi trở lên</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Muốn có (thêm) con:</label>
                <select
                  name="lifestyle.wantsChildren"
                  value={searchCriteria.lifestyle?.wantsChildren || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="yes">Có</option>
                  <option value="no">Không</option>
                  <option value="not_sure">Không chắc</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Có vật nuôi:</label>
                <select
                  name="lifestyle.hasPets"
                  value={searchCriteria.lifestyle?.hasPets || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="Bò sát">Bò sát</option>
                  <option value="Chim chóc">Chim chóc</option>
                  <option value="Chó">Chó</option>
                  <option value="Cá">Cá</option>
                  <option value="Hamster">Hamster</option>
                  <option value="Mèo">Mèo</option>
                  <option value="Ngựa">Ngựa</option>
                  <option value="Thú quý hiếm">Thú quý hiếm</option>
                  <option value="Thỏ">Thỏ</option>
                  <option value="Khác">Khác</option>
                  <option value="Không có vật nuôi">Không có vật nuôi</option>
                  <option value="Không thích nói">Không thích nói</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Nghề nghiệp:</label>
                <select
                  name="lifestyle.occupation"
                  value={searchCriteria.lifestyle?.occupation || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="Bán hàng / Tiếp thị">Bán hàng / Tiếp thị</option>
                  <option value="Chữa cháy / Hành pháp / Bảo vệ">Chữa cháy / Hành pháp / Bảo vệ</option>
                  <option value="Giao thông vận tải">Giao thông vận tải</option>
                  <option value="Hành chính / Thư ký">Hành chính / Thư ký</option>
                  <option value="Làm về pháp luật">Làm về pháp luật</option>
                  <option value="Người giúp việc">Người giúp việc</option>
                  <option value="Phi lợi nhuận / Giáo sĩ / Công tác xã hội">Phi lợi nhuận / Giáo sĩ / Công tác xã hội</option>
                  <option value="Sinh viên">Sinh viên</option>
                  <option value="Bán lẻ / Dịch vụ thực phẩm">Bán lẻ / Dịch vụ thực phẩm</option>
                  <option value="Công nghệ thông tin / Truyền thông">Công nghệ thông tin / Truyền thông</option>
                  <option value="Giáo dục">Giáo dục</option>
                  <option value="Không có việc làm / Ở nhà">Không có việc làm / Ở nhà</option>
                  <option value="Nghệ thuật / Sáng tạo / Biểu diễn">Nghệ thuật / Sáng tạo / Biểu diễn</option>
                  <option value="Người lao động / Công nhân nhà máy">Người lao động / Công nhân nhà máy</option>
                  <option value="Quân đội">Quân đội</option>
                  <option value="Thể thao / Giải trí">Thể thao / Giải trí</option>
                  <option value="Chính trị / Nhà nước / Dịch vụ dân sự">Chính trị / Nhà nước / Dịch vụ dân sự</option>
                  <option value="Du lịch / Khách sạn">Du lịch / Khách sạn</option>
                  <option value="Giải trí / Truyền thông">Giải trí / Truyền thông</option>
                  <option value="Kỹ thuật / Khoa học / Kỹ sư">Kỹ thuật / Khoa học / Kỹ sư</option>
                  <option value="Nghỉ hưu">Nghỉ hưu</option>
                  <option value="Nông nghiệp">Nông nghiệp</option>
                  <option value="Quảng cáo / Truyền thông">Quảng cáo / Truyền thông</option>
                  <option value="Thợ cắt tóc">Thợ cắt tóc</option>
                  <option value="Trông trẻ">Trông trẻ</option>
                  <option value="Tài chính / Ngân hàng / Tự kinh doanh">Tài chính / Ngân hàng / Tự kinh doanh</option>
                  <option value="Xây dựng / Giao dịch">Xây dựng / Giao dịch</option>
                  <option value="Y khoa / Nha khoa / Thú y">Y khoa / Nha khoa / Thú y</option>
                </select>
              </div>

              {/* Văn hóa */}
              <div>
                <label className="block text-gray-700 font-medium mb-1">Quốc tịch:</label>
                <select
                  name="culture.nationality"
                  value={searchCriteria.culture?.nationality || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="Việt Nam">Việt Nam</option>
                  <option value="Afghanistan">Afghanistan</option>
                  <option value="Ai Cập">Ai Cập</option>
                  <option value="Albania">Albania</option>
                  <option value="Algeria">Algeria</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Trình độ văn hóa:</label>
                <select
                  name="culture.education"
                  value={searchCriteria.culture?.education || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="Cấp 1">Cấp 1</option>
                  <option value="Bằng cấp 3 / Cấp 2">Bằng cấp 3 / Cấp 2</option>
                  <option value="Bằng trung học phổ thông">Bằng trung học phổ thông</option>
                  <option value="Bằng trung cấp nghề">Bằng trung cấp nghề</option>
                  <option value="Bằng cử nhân">Bằng cử nhân</option>
                  <option value="Bằng thạc sỹ">Bằng thạc sỹ</option>
                  <option value="Tiến sĩ">Tiến sĩ</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Ngôn ngữ chính:</label>
                <select
                  name="culture.primaryLanguage"
                  value={searchCriteria.culture?.primaryLanguage || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="Tiếng Việt">Tiếng Việt</option>
                  <option value="Tiếng Anh">Tiếng Anh</option>
                  <option value="Chinese (Mandarin)">Chinese (Mandarin)</option>
                  <option value="Tiếng Pháp">Tiếng Pháp</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Khả năng tiếng Anh:</label>
                <select
                  name="culture.englishAbility"
                  value={searchCriteria.culture?.englishAbility || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="Không">Không</option>
                  <option value="Thỉnh thoảng">Thỉnh thoảng</option>
                  <option value="Tốt">Tốt</option>
                  <option value="Rất tốt">Rất tốt</option>
                  <option value="Lưu loát">Lưu loát</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Khả năng tiếng Việt:</label>
                <select
                  name="culture.vietnameseAbility"
                  value={searchCriteria.culture?.vietnameseAbility || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="Không">Không</option>
                  <option value="Thỉnh thoảng">Thỉnh thoảng</option>
                  <option value="Tốt">Tốt</option>
                  <option value="Rất tốt">Rất tốt</option>
                  <option value="Lưu loát">Lưu loát</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Tôn giáo:</label>
                <select
                  name="culture.religion"
                  value={searchCriteria.culture?.religion || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="Phật giáo">Phật giáo</option>
                  <option value="Thiên Chúa giáo">Thiên Chúa giáo</option>
                  <option value="Hồi giáo">Hồi giáo</option>
                  <option value="Đạo giáo">Đạo giáo</option>
                  <option value="Tích-khắc giáo">Tích-khắc giáo</option>
                  <option value="Đạo Shintō">Đạo Shintō</option>
                  <option value="Không có đạo">Không có đạo</option>
                  <option value="Không thích nói">Không thích nói</option>
                </select>
              </div>

              {/* Chòm sao và năm sinh âm lịch */}
              <div>
                <label className="block text-gray-700 font-medium mb-1">Chòm sao:</label>
                <select
                  name="zodiacSign"
                  value={searchCriteria.zodiacSign || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="Bò Cạp">Bò Cạp</option>
                  <option value="Bảo Bình">Bảo Bình</option>
                  <option value="Cự Giải">Cự Giải</option>
                  <option value="Dương Cưu">Dương Cưu</option>
                  <option value="Kim Ngưu">Kim Ngưu</option>
                  <option value="Ma Kết">Ma Kết</option>
                  <option value="Nhân Mã">Nhân Mã</option>
                  <option value="Song Ngư">Song Ngư</option>
                  <option value="Song Sinh">Song Sinh</option>
                  <option value="Sư Tử">Sư Tử</option>
                  <option value="Thiên Bình">Thiên Bình</option>
                  <option value="Xử Nữ">Xử Nữ</option>
                  <option value="Không biết">Không biết</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Năm sinh âm lịch:</label>
                <select
                  name="lunarYear"
                  value={searchCriteria.lunarYear || ""}
                  onChange={handleCriteriaChange}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="">Bất kỳ</option>
                  <option value="Dần">Dần</option>
                  <option value="Dậu">Dậu</option>
                  <option value="Hợi">Hợi</option>
                  <option value="Mão">Mão</option>
                  <option value="Mùi">Mùi</option>
                  <option value="Ngọ">Ngọ</option>
                  <option value="Sửu">Sửu</option>
                  <option value="Thân">Thân</option>
                  <option value="Thìn">Thìn</option>
                  <option value="Tuất">Tuất</option>
                  <option value="Tý">Tý</option>
                  <option value="Tỵ">Tỵ</option>
                  <option value="Không biết">Không biết</option>
                </select>
              </div>
            </div>

            {/* Nút tìm kiếm và lưu */}
            <div className="flex justify-end gap-4 mt-6">
              {editingSearch ? (
                <>
                  <button
                    onClick={handleUpdateSearch}
                    disabled={loading}
                    className={`px-4 py-2 rounded-lg shadow ${
                      loading
                        ? "bg-blue-300 cursor-not-allowed"
                        : "bg-blue-500 text-white hover:bg-blue-600"
                    }`}
                  >
                    Cập nhật tìm kiếm
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={loading}
                    className={`px-4 py-2 rounded-lg shadow ${
                      loading
                        ? "bg-gray-300 cursor-not-allowed"
                        : "bg-gray-500 text-white hover:bg-gray-600"
                    }`}
                  >
                    Hủy
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleSaveSearch}
                    disabled={loading}
                    className={`px-4 py-2 rounded-lg shadow ${
                      loading
                        ? "bg-blue-300 cursor-not-allowed"
                        : "bg-blue-500 text-white hover:bg-blue-600"
                    }`}
                  >
                    Lưu tìm kiếm
                  </button>
                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    className={`px-4 py-2 rounded-lg shadow ${
                      loading
                        ? "bg-green-300 cursor-not-allowed"
                        : "bg-green-500 text-white hover:bg-green-600"
                    }`}
                  >
                    Tìm kiếm
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Tab 2: Tìm kiếm đã lưu */}
        {activeTab === "saved" && (
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <h3 className="text-2xl font-semibold text-gray-800 mb-6">Danh Sách Tìm Kiếm Đã Lưu</h3>
            {savedSearches.length === 0 ? (
              <p className="text-center text-gray-500">Bạn chưa lưu tìm kiếm nào.</p>
            ) : (
              <ul className="space-y-4">
                {savedSearches.map((search, index) => (
                  <li
                    key={index}
                    className="flex justify-between items-center p-4 bg-gray-100 rounded-lg shadow-sm hover:bg-gray-200 transition-colors"
                  >
                    <div>
                      <p className="font-semibold text-gray-800">{search.name}</p>
                      <p className="text-sm text-gray-600">
                        Được lưu vào: {new Date(search.createdAt).toLocaleString("vi-VN")}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSelectSavedSearch(search.criteria)}
                        className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        Tìm kiếm
                      </button>
                      <button
                        onClick={() => handleEditSavedSearch(index)}
                        className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                      >
                        Sửa
                      </button>
                      <button
                        onClick={() => handleDeleteSavedSearch(index)}
                        className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                      >
                        Xóa
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Tab 3: Tìm kiếm phổ biến */}
        {activeTab === "popular" && (
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <h3 className="text-2xl font-semibold text-gray-800 mb-6">Tìm Kiếm Phổ Biến</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => handlePopularSearch("new_members")}
                className="p-4 bg-gray-100 rounded-lg shadow-sm hover:bg-gray-200 transition-colors text-left"
              >
                <p className="font-semibold text-gray-800">Thành viên mới</p>
                <p className="text-sm text-gray-600">Hiển thị thành viên mới đăng ký</p>
              </button>
              <button
                onClick={() => handlePopularSearch("matching_criteria")}
                className="p-4 bg-gray-100 rounded-lg shadow-sm hover:bg-gray-200 transition-colors text-left"
              >
                <p className="font-semibold text-gray-800">Người phù hợp</p>
                <p className="text-sm text-gray-600">Hiển thị người có tiêu chí phù hợp</p>
              </button>
              <button
                onClick={() => handlePopularSearch("latest_photos")}
                className="p-4 bg-gray-100 rounded-lg shadow-sm hover:bg-gray-200 transition-colors text-left"
              >
                <p className="font-semibold text-gray-800">Hình mới nhất</p>
                <p className="text-sm text-gray-600">Hiển thị người dùng có ảnh mới nhất</p>
              </button>
              <button
                onClick={() => handlePopularSearch("similar_lifestyle")}
                className="p-4 bg-gray-100 rounded-lg shadow-sm hover:bg-gray-200 transition-colors text-left"
              >
                <p className="font-semibold text-gray-800">Ưa thích nhất</p>
                <p className="text-sm text-gray-600">Hiển thị người có cùng sở thích</p>
              </button>
              <button
                onClick={() => handlePopularSearch("same_location")}
                className="p-4 bg-gray-100 rounded-lg shadow-sm hover:bg-gray-200 transition-colors text-left"
              >
                <p className="font-semibold text-gray-800">Trong khu vực tôi</p>
                <p className="text-sm text-gray-600">Hiển thị người ở cùng phường/xã, quận/huyện</p>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvancedSearch;